import { useEffect, useState } from "react";
import { getBreathConfiguration } from "../../../../common/network/Api";
import { PnoeMeasurementCatalogs } from "../../entities/pnoeMeasurement";

/**
 * Display-label catalogs for the four measurement enums.
 *
 * Lifted out of the shared configuration payload, so this costs no extra request
 * beyond the one `usePnoeParameterOptions` already makes. A failure is not fatal
 * — every label resolver in `pnoeMeasurementStatus` falls back to hardcoded
 * English — so this hook swallows the error rather than surfacing it.
 */
export const usePnoeCatalogs = () => {
  const [catalogs, setCatalogs] = useState<PnoeMeasurementCatalogs | null>(null);

  useEffect(() => {
    let cancelled = false;
    getBreathConfiguration()
      .then((data) => {
        if (!cancelled && data?.catalogs) setCatalogs(data.catalogs);
      })
      .catch((error) => {
        console.warn("Error loading breath configuration:", error);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return catalogs;
};

/**
 * Turns an axios failure into something a user can act on. The Breath Data API
 * is a separate service from the platform API, so the two failures worth calling
 * out by name are "your session isn't accepted here" and "the browser never got
 * a response" (CORS or the service being down).
 */
export const describeBreathError = (error: any): string => {
  const status = error?.response?.status;
  if (status === 401 || status === 403) {
    return "Your session isn't authorised for the breath-test service.";
  }
  if (status === 422) {
    return "The breath-test service rejected these filters.";
  }
  if (!error?.response) {
    return "Couldn't reach the breath-test service.";
  }
  return "Couldn't load PNOĒ tests.";
};
